import React from "react";
import { FileText, RefreshCw } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { DriveSyncStatus } from "@/lib/googleDriveKbLink";

type GoogleDriveFileRowProps = {
  id: string;
  name: string;
  modifiedTime?: string;
  selected: boolean;
  syncStatus: DriveSyncStatus;
  onToggle: (id: string) => void;
};

const STATUS_LABEL: Record<DriveSyncStatus, string> = {
  new: "New",
  synced: "In Library",
  stale: "Updated on Drive",
};

const STATUS_VARIANT: Record<
  DriveSyncStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  new: "outline",
  synced: "secondary",
  stale: "destructive",
};

export const GoogleDriveFileRow = React.memo(function GoogleDriveFileRow({
  id,
  name,
  modifiedTime,
  selected,
  syncStatus,
  onToggle,
}: GoogleDriveFileRowProps) {
  return (
    <div
      className={cn(
        "flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm",
        "hover:bg-muted/60 transition-colors",
        selected && "bg-muted/40"
      )}
      data-testid={`drive-file-${id}`}
    >
      <Checkbox
        checked={selected}
        onCheckedChange={() => onToggle(id)}
        aria-label={`Select ${name}`}
      />
      <FileText className="h-4 w-4 shrink-0 text-blue-500" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{name}</p>
        {modifiedTime && (
          <p className="truncate text-xs text-muted-foreground">
            {new Date(modifiedTime).toLocaleString()}
          </p>
        )}
      </div>
      <Badge
        variant={STATUS_VARIANT[syncStatus]}
        className="shrink-0 gap-1 text-[10px]"
      >
        {syncStatus === "stale" && <RefreshCw className="h-3 w-3" />}
        {STATUS_LABEL[syncStatus]}
      </Badge>
    </div>
  );
});
